import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import bookingService from '../services/bookingService';

function StaffScheduleModal({ staff, onClose }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    staff_id: staff.staff_id,
    booking_buffer_minutes: 10,
    advance_booking_days: 30,
    working_hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  useEffect(() => {
    loadStaffSchedule();
  }, []);

  const loadStaffSchedule = async () => {
    try {
      const avail = await bookingService.getStaffAvailability(staff.staff_id);
      setFormData({
        staff_id: staff.staff_id,
        booking_buffer_minutes: avail.booking_buffer_minutes || 10,
        advance_booking_days: avail.advance_booking_days || 30,
        working_hours: avail.working_hours
      });
    } catch (error) {
      console.log('No existing schedule for staff:', staff.staff_id);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await bookingService.updateStaffAvailability(staff.staff_id, formData);
      showToast('Schedule saved successfully!');
      onClose();
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          await bookingService.createStaffAvailability(formData);
          showToast('Schedule created successfully!');
          onClose();
        } catch (createError) {
          showToast('Failed to save schedule', 'error');
        }
      } else {
        showToast('Failed to save schedule', 'error');
      }
    }
  };

  const updateDayHours = (day, index, field, value) => {
    const newHours = { ...formData.working_hours };
    if (!newHours[day][index]) {
      newHours[day][index] = { start: '', end: '' };
    }
    newHours[day][index][field] = value;
    setFormData({ ...formData, working_hours: newHours });
  };

  const addDaySlot = (day) => {
    const newHours = { ...formData.working_hours };
    newHours[day].push({ start: '09:00', end: '17:00' });
    setFormData({ ...formData, working_hours: newHours });
  };

  const removeDaySlot = (day, index) => {
    const newHours = { ...formData.working_hours };
    newHours[day].splice(index, 1);
    setFormData({ ...formData, working_hours: newHours });
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Availability for {staff.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="fa fa-times text-xl"></i>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Buffer Between Bookings (minutes)</label>
                <input
                  type="number"
                  value={formData.booking_buffer_minutes}
                  onChange={(e) => setFormData({ ...formData, booking_buffer_minutes: parseInt(e.target.value) })}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Advance Booking (days)</label>
                <input
                  type="number"
                  value={formData.advance_booking_days}
                  onChange={(e) => setFormData({ ...formData, advance_booking_days: parseInt(e.target.value) })}
                  className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-3">Working Hours</label>
              {days.map(day => (
                <div key={day} className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium capitalize text-sm">{day}</span>
                    <button
                      type="button"
                      onClick={() => addDaySlot(day)}
                      className="text-sm text-blue-500 hover:text-blue-700 font-medium"
                    >
                      + Add Slot
                    </button>
                  </div>
                  {formData.working_hours[day].length === 0 ? (
                    <span className="text-sm text-gray-500">Unavailable</span>
                  ) : (
                    formData.working_hours[day].map((slot, idx) => (
                      <div key={idx} className="flex gap-2 items-center mb-2">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateDayHours(day, idx, 'start', e.target.value)}
                          className="bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                        />
                        <span className="text-sm">to</span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateDayHours(day, idx, 'end', e.target.value)}
                          className="bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                        />
                        <button
                          type="button"
                          onClick={() => removeDaySlot(day, idx)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="bg-white text-gray-600 border border-gray-200 px-4 py-2 rounded text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
              >
                Save Schedule
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default StaffScheduleModal;
